import { DatePicker } from 'antd';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import React, { FC, HTMLAttributes, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import CustomDropdown from '../../../components/reusable-ui/custom-dropdown';
import { NEXT, PREV } from '../../../components/reusable-ui/custom-pagination';
import { METHODS } from '../../../data/api-method-constants';
import { DATE_TIME_FORMATS } from '../../../data/constants';
import { convertArrayToObject } from '../../../helpers/catalog-helper';
import { getActiveBranches, isSourcePos } from '../../../helpers/common-helper';
import { fetchCommonData, getAllUsers } from '../../../redux/slices';
import { ENDPOINT } from '../../../services/apiUrls';
import Settlements from './settlements';
import ViewSettlement from './view-settlement';
import ZeroScreen from './zero-screen';

interface deviceType {
  branchName: string;
  deviceId: string;
  label: string;
  lastLoginTime: string;
  lastUserName: any;
  serialNumber: number;
  status: string;
}

interface deviceFilterPayload {
  branchIds: string[];
  pageSize: number;
  lastKey?: any;
}

interface searchFilterType {
  balanceDue?: boolean;
  status: string;
  pageSize: number;
  branchId: string;
  date?: string;
  buid?: string;
  deviceId?: any;
  deviceLabel?: string;
  lastKey?: any;
}

interface paginationType {
  lastId?: string;
  lastTimestamp?: string;
}
interface deviceDataMap {
  [key: string]: deviceType;
}

let intialPagination: paginationType = {};

const CashierSettlement: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const [settlements, setSettlements] = useState([]);
  const [prevPagination, setPrevPagination] = useState<any>([]);
  const [paginationData, setPaginationData] = useState(intialPagination);

  const userData = useSelector((state: any) => state.businessReducer.userData);
  const allowedBusinessBranchesDataMap = convertArrayToObject(
    userData.permittedBranches,
    "branchId"
  );
  const currentBusinessId = useSelector(
    (state: any) => state.businessReducer?.businessData?.branchId
  );
  const allUsersDataMap = useSelector(
    (state: any) => state.outletStatusReducer.allUsersMap
  );
  let [deviceDataMap, setDeviceDataMap] = useState({} as deviceDataMap);

  let intialSearchState: searchFilterType = {
    balanceDue: false,
    status: 'Open',
    pageSize: 10,
    branchId: getDefaultBusinessId(),
    date: '',
    buid: '',
    deviceId: '',
    deviceLabel: '',
    lastKey: {},
  };

  const [state, setState] = useState({
    settlementId: '',
    viewSettlement: false,
    searchFilters: intialSearchState,
    devicePagination: {},
  });

  const DROPDOWN_CONFIG = {
    branches: {
      dropdownHeading: 'Branch',
      isMandatory: false,
      parentClass: `w-[15%] sm:w-full `,
      optionsMap: getActiveBranches(allowedBusinessBranchesDataMap),
      onSubmit: (value: any) => handleFilter(value[0], 'branchId'),
      uniqueKey: 'branchId',
      preSelectedValues: state.searchFilters.branchId,
      placeholder: 'Select branch',
      getListItemText: (data: any) => data?.branchName || '',
    },
    users: {
      optionsMap: allUsersDataMap,
      placeholder: state.searchFilters.buid ? 'Reset' : 'Select User',
      uniqueKey: 'businessUserId',
      addEmptyOption: state.searchFilters.buid ? true : false,
      parentClass: 'w-[15%] ml-20 sm:mt-5 sm:ml-0 sm:w-full ',
      key: 'businessUserId',
      onSubmit: (value: any) => handleFilter(value[0], 'buid'),
      preSelectedValues: state.searchFilters.buid,
      getListItemText: (data: any) => data?.name?.firstName || '',
    },
    device: {
      optionsMap: deviceDataMap,
      placeholder: state.searchFilters.deviceId ? 'Reset' : 'Select Device',
      uniqueKey: 'deviceId',
      parentClass: 'w-[15%] ml-20 sm:ml-0 sm:w-full sm:mt-[-23px] ',
      onSubmit: (value: any) => handleFilter(value[0], 'deviceId'),
      preSelectedValues: state.searchFilters.deviceId,
      key: 'deviceId',
      isShowLoadMore:
        state.devicePagination &&
        Object.keys(state.devicePagination)?.length > 0,
      onLoadMoreClick: getDevices,
      addEmptyOption: state.searchFilters.deviceId ? true : false,
      getListItemText: (data: any) =>
        data?.label || '# ' + data?.serialNumber || '',
    },
  };

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getAllUsers());
  }, []);

  useEffect(() => {
    setDeviceDataMap({});
    getDevices();
  }, [state.searchFilters.branchId]);

  useEffect(() => {
    getSettlements();
  }, [state.searchFilters]);

  function getDefaultBusinessId() {
    if (isSourcePos()) return currentBusinessId;
    return allowedBusinessBranchesDataMap &&
      Object.keys(allowedBusinessBranchesDataMap)?.length > 0
      ? Object.keys(allowedBusinessBranchesDataMap)[0]
      : "";
  }

  function handleDevices(res: any) {
    setState({ ...state, devicePagination: res.lastKey || {} });
    let newDataMap = cloneDeep(convertArrayToObject(res?.results, 'deviceId'));
    setDeviceDataMap(
      res.results?.length ? Object.assign(newDataMap, deviceDataMap) : {}
    );
  }

  function getDevices() {
    let payload = cloneDeep({
      branchIds: [state.searchFilters.branchId],
      pageSize: 20,
    }) as deviceFilterPayload;
    if (Object.keys(state.devicePagination).length) {
      payload = { ...payload, lastKey: state.devicePagination };
    }
    dispatch(
      fetchCommonData({
        method: METHODS.POST,
        body: payload,
        url: ENDPOINT.ACCOUNTING.getDevices,
        callback: handleDevices,
      })
    );
  }

  function getSettlements(data = '') {
    let pagination_key = {};
    if (data === NEXT ) {
      pagination_key = paginationData;
    } else if (data === PREV ) {
      let newStack = prevPagination.slice(0, -1);
      pagination_key = newStack.length ? newStack[newStack.length - 1] : {};
      setPrevPagination(newStack);
    }
    let payload = cloneDeep(state.searchFilters);
    if (Object.keys(pagination_key).length) {
      payload = { ...payload, lastKey: pagination_key };
    } else {
      delete payload['lastKey'];
    }
    if (state.searchFilters.status === 'Due') {
      payload['balanceDue'] = true;
      payload['status'] = '';
    } else {
      payload['balanceDue'] = false;
    }
    payload['deviceLabel'] = deviceDataMap[payload['deviceId']]?.label || '';
    dispatch(
      fetchCommonData({
        method: METHODS.POST,
        body: payload,
        url: ENDPOINT.ACCOUNTING.getSettlements,
        callback: (res: any) => handleSettlements(res, data),
      })
    );
  }

  function handleStatus(status: string) {
    setPrevPagination([]);
    setPaginationData({});
    setState({
      ...state,
      searchFilters: {
        ...state.searchFilters,
        buid: '',
        date: '',
        deviceId: '',
        deviceLabel: '',
        status,
      },
    });
  }

  function handleSettlements(response: any, data = '') {
    if (data === NEXT ) {
      if (paginationData.lastId !== response?.lastKey?.lastId)
        setPrevPagination([...prevPagination, paginationData]);
    }
    setPaginationData(response.lastKey || {});
    setSettlements(response.results || []);
  }

  function renderCustomDropDown(dropdownConfig: any) {
    return <CustomDropdown {...dropdownConfig} />;
  }

  function toggleViewSettlement(id?: any) {
    let toggleState = id ? true : false;
    setState({ ...state, settlementId: id, viewSettlement: toggleState });
  }

  const onDateRangeSubmission = (data: any) => {
    handleFilter(
      data ? moment(data).format(DATE_TIME_FORMATS.yearMonthDay) : '',
      'date'
    );
  };

  function handleFilter(value: string, name: string) {
    setPaginationData({});
    setPrevPagination([]);
    let filter = {} as searchFilterType;
    if (name !== 'branchId') {
      if (name === 'status') {
        filter = { ...intialSearchState, [name]: value };
      } else if (value === '') {
        filter = { ...intialSearchState, status: 'Open', [name]: value };
      } else filter = { ...intialSearchState, status: '', [name]: value };
    } else {
      filter = { ...state.searchFilters, [name]: value };
    }
    setState({ ...state, searchFilters: filter });
  }

  function renderFilters() {
    return (
      <div className="flex sm:flex-col w-full p-20 sm:py-12 sm:px-16 sm:pb-0 bg-white">
        {renderCustomDropDown(DROPDOWN_CONFIG.branches)}
        <DatePicker
          className="h-50 w-[15%] ml-20 sm:ml-0 sm:mt-[-23px] sm:w-full rounded-[8px] border-[#EBEBEB]"
          format={DATE_TIME_FORMATS.dayMonthYear}
          value={state.searchFilters.date ? moment(state.searchFilters.date) : undefined }
          onChange={onDateRangeSubmission}
        />
        {renderCustomDropDown(DROPDOWN_CONFIG.users)}
        {renderCustomDropDown(DROPDOWN_CONFIG.device)}
      </div>
    );
  }

  function noFiltersApplied() {
    let { searchFilters } = state;
    return (
      searchFilters.buid === '' &&
      searchFilters.date === '' &&
      searchFilters.deviceId === '' &&
      searchFilters.status === 'Open' &&
      searchFilters.branchId === ''
    );
  }

  function renderSettlements() {
    return (
      <div className="h-full">
        {(settlements?.length > 0 || !noFiltersApplied()) && (
          <>
            {renderFilters()}
            <Settlements
              prevPagination={prevPagination}
              paginationData={paginationData}
              getSettlements={getSettlements}
              status={state.searchFilters.status}
              handleStatus={handleStatus}
              data={settlements}
              toggleViewSettlement={toggleViewSettlement}
            />
          </>
        )}
        {settlements?.length === 0 && noFiltersApplied() && <ZeroScreen />}
      </div>
    );
  }

  return !state.viewSettlement ? (
    renderSettlements()
  ) : (
    <ViewSettlement
      toggleViewSettlement={toggleViewSettlement}
      settlementId={state.settlementId}
    />
  );
};
export default CashierSettlement;
